import axios from 'axios';
import {ENDPOINT_URL, GET_CLIENT_LIST} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	rankLookupCandidates,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';
import {resolveByFields} from '../../core/fieldSearch';
import {readLookupPages} from '../../core/lookupPages';
import {clientMac, normalizeClient} from './reference';

/**
 * Resolve Clients from the authenticated Canvas API.
 *
 * The server's own search is used rather than a local scan, because the
 * estate runs to five figures and the table only ever holds one page. What
 * that search covers was measured rather than assumed: username, MAC, SSID,
 * AP name, Site and both IP addresses all match, and a nonsense string
 * returns nothing - so a miss here is a real miss.
 *
 * Two things it will not do, which is why neither is offered as a lookup:
 * the frequency band and the connection type match nothing at all, and two
 * terms never combine - "Jio_Private_Net Shop8" returns zero rows. One
 * query, one field.
 *
 * @param {object} params resolver parameters
 * @param {object} pageContext the page the request was made from
 * @param {object} envelope command cancellation context
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveClients(params = {}, pageContext = {}, envelope = {}) {
	const query = String(params.query || '').trim();
	const requestedLimit = Number(params.limit);
	const limit = Number.isInteger(requestedLimit)
		? Math.min(Math.max(requestedLimit, 1), LOOKUP_LIMIT)
		: LOOKUP_LIMIT;
	// The Site the request named, or the Site the page is already scoped to.
	const siteId = params.site_id ??
		(params.current_site ? pageContext?.site_id : null);
	let url = `${ENDPOINT_URL}${GET_CLIENT_LIST}` +
		`?limit=${LOOKUP_LIMIT * 4}&offset=0`;
	if (siteId) url += `&group=${encodeURIComponent(String(siteId))}`;
	if (query) url += `&searchfilter=${encodeURIComponent(query)}`;
	const rows = await readLookupPages({
		preview: params.browse === true && !query,
		pageSize: LOOKUP_LIMIT * 4,
		rowsFrom: (document) => Array.isArray(document?.results) ? document.results : [],
		fetchPage: async (offset) => {
			const {data} = await axios.get(url.replace('&offset=0', `&offset=${offset}`), {
				signal: envelope.signal, timeout: 3000,
			});
			return decodeAgentResponse(data) || {};
		},
	});
	replaceResolvedEntities('client', rows, clientMac);
	return {
		candidates: resolveByFields({
			rows,
			normalizeRow: normalizeClient,
			query,
			limit,
			searchFields: params.search_fields,
			rank: rankLookupCandidates,
			fuzzy: params.fuzzy === true,
		}),
	};
}

/**
 * The Canvas row for a resolved client, kept from the lookup that found it.
 * @param {string} mac client MAC address
 * @return {object|null} Canvas client row
 */
export function getResolvedClient(mac) {
	return getResolvedEntity('client', mac);
}
